// 권위: docs/ARCH_DASHBOARD.md §4.4 에이전트 실행 이력 화면.
// 화면 8 (SystemLog) 과 동일한 system_events 원천을 공유하지만, 운영자가 에이전트·기간 중심으로
// 조회하도록 **기간 preset** 과 **agent multi-select** 을 강화한 필터 타입이다.
//
// 구현 방침:
//   - SystemLogFilters 를 확장하기보다 "agent-history 전용" 으로 새로 정의한다.
//     이유: preset 은 from/to 와 합성되며, 쿼리스트링 포맷도 화면 4 관점에서 재해석한다.
//   - Severity·Source·EventCode·q·unacked 의미는 화면 8 과 동일.
//   - 백엔드 EventRepository 는 trace_id 필터를 지원하지 않으므로, trace_id 연관 체인은
//     **클라이언트 측에서** 현재 아이템의 traceId 로 동일 trace 의 최근 N건을 조회해 필터링한다.

import {
  ALL_SEVERITIES,
  PAGE_SIZES,
  type Severity,
} from '../SystemLog/systemLog.types';

export type DateRangePreset = 'today' | '1w' | '1m' | '3m' | 'custom';

// 화면 4 의 에이전트 드롭다운 — docs/ARCH_DASHBOARD.md §4.4 "에이전트 선택".
// SOURCE_AGENTS (화면 8) 대비 LLM 범주를 A~F 전체로 확장.
export const AGENT_OPTIONS: string[] = [
  'governance',
  'leading',
  'trading',
  'short',
  'llm_a',
  'llm_b',
  'llm_c',
  'llm_d',
  'llm_e',
  'llm_f',
  'data',
  'broker',
  'obs',
  'dashboard',
  'batch',
];

export interface AgentHistoryFilters {
  severities: Severity[];
  agents: string[];          // source_agent multi-select
  eventCode: string;         // glob (* 허용)
  preset: DateRangePreset;
  from: string;              // datetime-local ("" = 미설정) — preset === 'custom' 일 때만 직접 입력
  to: string;
  q: string;                 // summary full-text
  unackedOnly: boolean;
  traceId: string;           // 연관 이벤트 체인 필터 (클라이언트 측 필터링)
  runId: string;             // run_id 필터 (entity_refs 또는 summary 포함 매칭, 클라이언트 측)
  page: number;
  pageSize: number;
}

export const DEFAULT_AGENT_HISTORY_FILTERS: AgentHistoryFilters = {
  severities: [],
  agents: [],
  eventCode: '',
  preset: '1w',
  from: '',
  to: '',
  q: '',
  unackedOnly: false,
  traceId: '',
  runId: '',
  page: 0,
  pageSize: 100,
};

/** preset → (from, to) 로 변환. 'custom' 은 f.from/f.to 를 그대로 사용. */
function resolveRange(f: AgentHistoryFilters, now: Date = new Date()): { from?: string; to?: string } {
  if (f.preset === 'custom') {
    return {
      from: f.from ? toIso(f.from) : undefined,
      to: f.to ? toIso(f.to) : undefined,
    };
  }
  const to = now;
  const from = new Date(now);
  switch (f.preset) {
    case 'today':
      from.setHours(0, 0, 0, 0);
      break;
    case '1w':
      from.setDate(from.getDate() - 7);
      break;
    case '1m':
      from.setMonth(from.getMonth() - 1);
      break;
    case '3m':
      from.setMonth(from.getMonth() - 3);
      break;
  }
  return {
    from: from.toISOString(),
    to: to.toISOString(),
  };
}

/**
 * 필터 → listEvents 쿼리 객체. trace_id / run_id 는 **백엔드 미지원**이므로 쿼리에 싣지 않는다.
 * 클라이언트 훅 (useAgentHistoryList) 에서 응답 후 filter 로 좁힌다.
 */
export function filtersToEventQuery(f: AgentHistoryFilters, now: Date = new Date()) {
  const range = resolveRange(f, now);
  return {
    severity: f.severities.length > 0 ? f.severities.join(',') : undefined,
    source: f.agents.length > 0 ? f.agents.join(',') : undefined,
    eventCode: f.eventCode.trim() || undefined,
    from: range.from,
    to: range.to,
    q: f.q.trim() || undefined,
    unacked: f.unackedOnly ? true : undefined,
    page: f.page,
    pageSize: f.pageSize,
  };
}

/** URL 쿼리스트링 동기화 — 기본값과 동일한 항목은 생략. */
export function filtersToSearchParams(f: AgentHistoryFilters): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.severities.length > 0) sp.set('severity', f.severities.join(','));
  if (f.agents.length > 0) sp.set('agent', f.agents.join(','));
  if (f.eventCode.trim()) sp.set('event_code', f.eventCode.trim());
  if (f.preset !== DEFAULT_AGENT_HISTORY_FILTERS.preset) sp.set('preset', f.preset);
  if (f.preset === 'custom') {
    if (f.from) sp.set('from', f.from);
    if (f.to) sp.set('to', f.to);
  }
  if (f.q.trim()) sp.set('q', f.q.trim());
  if (f.unackedOnly) sp.set('unacked', '1');
  if (f.traceId.trim()) sp.set('trace_id', f.traceId.trim());
  if (f.runId.trim()) sp.set('run_id', f.runId.trim());
  if (f.page > 0) sp.set('page', String(f.page));
  if (f.pageSize !== DEFAULT_AGENT_HISTORY_FILTERS.pageSize) sp.set('page_size', String(f.pageSize));
  return sp;
}

export function filtersFromSearchParams(sp: URLSearchParams): AgentHistoryFilters {
  const sev = (sp.get('severity') ?? '')
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter((s): s is Severity => (ALL_SEVERITIES as string[]).includes(s));

  const agents = (sp.get('agent') ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const preset = (sp.get('preset') ?? DEFAULT_AGENT_HISTORY_FILTERS.preset) as DateRangePreset;
  const validPreset: DateRangePreset = ['today', '1w', '1m', '3m', 'custom'].includes(preset)
    ? preset
    : DEFAULT_AGENT_HISTORY_FILTERS.preset;

  const pageSize = parseInt(sp.get('page_size') ?? '', 10);
  const page = parseInt(sp.get('page') ?? '', 10);

  return {
    severities: sev,
    agents,
    eventCode: sp.get('event_code') ?? '',
    preset: validPreset,
    from: sp.get('from') ?? '',
    to: sp.get('to') ?? '',
    q: sp.get('q') ?? '',
    unackedOnly: sp.get('unacked') === '1' || sp.get('unacked') === 'true',
    traceId: sp.get('trace_id') ?? '',
    runId: sp.get('run_id') ?? '',
    page: Number.isFinite(page) && page > 0 ? page : 0,
    pageSize: PAGE_SIZES.includes(pageSize) ? pageSize : DEFAULT_AGENT_HISTORY_FILTERS.pageSize,
  };
}

function toIso(dtLocal: string): string {
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dtLocal)) return `${dtLocal}:00Z`;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(dtLocal)) return `${dtLocal}Z`;
  return dtLocal;
}
